import { type PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type { Experience, ExperienceFilters } from "@/Dashboard/pages/ExperiencesV2/types";
import { filterExperiences } from "@/Dashboard/pages/ExperiencesV2/utils/experienceFilters";
import { sortExperiences } from "@/Dashboard/pages/ExperiencesV2/utils/experienceSort";
import { REQUEST_STATUSES } from "@/constants";
import type { EXPERIENCE_CATEGORY } from "@/constants/experience";
import { ExperienceService, FavoriteService } from "@/services";
import type { selectUser } from "@/store/auth/auth.slice";
import type { State } from "@/store/types";

interface ExperiencesState {
  [propertyId: number]: typeof defaultState;
}

const defaultState = {
  data: [] as Experience[],
  status: REQUEST_STATUSES.IDLE as REQUEST_STATUSES,
  err: null as unknown,
  noResults: false as boolean,
} as const;

const initialState: ExperiencesState = {};

export const fetchExperiencesV2 = createAsyncThunk(
  "experiencesV2/fetchExperiences",
  async ({ propertyId, query }: { propertyId: number; query?: unknown }) => {
    const { resp, status, err } = await ExperienceService.getExperiences(propertyId, query);
    const { data: experiences = [], count = 0 } = resp || {};

    return {
      propertyId,
      experiences,
      count,
      status,
      err,
      query,
    };
  },
);

export const addFavoriteV2 = createAsyncThunk(
  "experiencesV2/addFavorite",
  async ({ propertyId, experienceId, userId }: { propertyId: number; experienceId: number; userId: number }) => {
    const { resp, status, err } = await FavoriteService.addFavorite(propertyId, experienceId);

    if (status === REQUEST_STATUSES.SUCCEEDED) {
      return {
        propertyId,
        experienceId,
        userId,
        favoriteId: resp?.id,
      };
    }

    const errorMessage =
      err && typeof err === "object" && "message" in err
        ? (err as { message: string }).message
        : typeof err === "string"
          ? err
          : "Failed to add favorite";
    throw new Error(errorMessage);
  },
);

export const removeFavoriteV2 = createAsyncThunk(
  "experiencesV2/removeFavorite",
  async ({ propertyId, experienceId, userId }: { propertyId: number; experienceId: number; userId: number }) => {
    const { status, err } = await FavoriteService.deleteFavorite(propertyId, experienceId);

    if (status === REQUEST_STATUSES.SUCCEEDED) {
      return {
        propertyId,
        experienceId,
        userId,
      };
    }

    const errorMessage =
      err && typeof err === "object" && "message" in err
        ? (err as { message: string }).message
        : typeof err === "string"
          ? err
          : "Failed to remove favorite";
    throw new Error(errorMessage);
  },
);

const experiencesV2Slice = createSlice({
  name: "experiencesV2",
  initialState,
  reducers: {
    updateExperience: (state, action: PayloadAction<{ propertyId: number; experience: Experience }>) => {
      const { propertyId, experience } = action.payload;
      const data = state[propertyId]?.data || [];
      const index = data.findIndex((x) => x.id === experience.id);
      if (index !== -1) {
        data[index] = experience;
        state[propertyId].data = [...data];
      }
    },

    deleteExperiences: (state, action: PayloadAction<{ propertyId: number; experienceIds: number[] }>) => {
      const { propertyId, experienceIds } = action.payload;
      if (state[propertyId]) {
        state[propertyId].data = state[propertyId].data.filter((experience) => !experienceIds.includes(experience.id));
      }
    },

    resetExperiences: (state, action: PayloadAction<{ propertyId: number }>) => {
      const { propertyId } = action.payload;
      delete state[propertyId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExperiencesV2.pending, (state, action) => {
        const propertyId = action.meta.arg.propertyId;
        if (!state[propertyId]) {
          state[propertyId] = { ...defaultState };
        }
        state[propertyId].status = REQUEST_STATUSES.LOADING;
        state[propertyId].err = null;
      })
      .addCase(fetchExperiencesV2.fulfilled, (state, action) => {
        const { propertyId, experiences, status, err } = action.payload;
        state[propertyId] = {
          ...state[propertyId],
          data: experiences,
          status,
          err,
          noResults: experiences.length === 0,
        };
      })
      .addCase(fetchExperiencesV2.rejected, (state, action) => {
        const propertyId = action.meta.arg.propertyId;
        if (state[propertyId]) {
          state[propertyId].status = REQUEST_STATUSES.FAILED;
          state[propertyId].err = action.error;
        }
      })
      .addCase(addFavoriteV2.fulfilled, (state, action) => {
        const { propertyId, experienceId, userId, favoriteId } = action.payload;
        const data = state[propertyId]?.data || [];
        const index = data.findIndex((x) => x.id === experienceId);
        if (index !== -1) {
          (data[index] as Experience).favorites = [{ id: favoriteId, userId, experienceId, propertyId }];
        }
      })
      .addCase(removeFavoriteV2.fulfilled, (state, action) => {
        const { propertyId, experienceId } = action.payload;
        const data = state[propertyId]?.data || [];
        const index = data.findIndex((x) => x.id === experienceId);
        if (index !== -1) {
          (data[index] as Experience).favorites = [];
        }
      });
  },
});

export const experiencesV2Selector = (propertyId: number) => {
  return (state: State) => ({
    ...defaultState,
    ...state.experiencesV2[propertyId],
  });
};

export const selectFilteredAndSortedExperiences = ({
  propertyId,
  experienceCategory,
  filters,
  searchText,
  sortField,
  sortOrientation,
  user,
}: {
  propertyId: number;
  experienceCategory: EXPERIENCE_CATEGORY;
  filters: ExperienceFilters;
  searchText: string;
  sortField: string;
  sortOrientation: string;
  user: ReturnType<typeof selectUser>;
}) => {
  return (state: State) => {
    const { data = [] } = state.experiencesV2[propertyId] || {};

    const filtered = filterExperiences(data, experienceCategory, filters, searchText, user);

    return sortExperiences(filtered, sortField, sortOrientation);
  };
};

export default experiencesV2Slice.reducer;
